import React, { useEffect, useState } from 'react';
import {
  displayStatus,
  errorMessage,
  evaluationPercentage,
  percentageLabel,
} from '../../lib/evaluationIntegrity';
import { ScoreSummary } from '../../components/EvaluationIntegrity';
import { useNavigate } from 'react-router-dom';
import { evaluationsAPI, projectsAPI, eventsAPI } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import SemaforoBadge from '../../components/SemaforoBadge';
import StatusBadge from '../../components/StatusBadge';
import ProjectHierarchy from '../../components/cliente/ProjectHierarchy';
import { VersionTrendChart } from '../../components/ScoreChart';
import { getEvalIncidents } from '../../lib/evalSections';
import { getSemaforoBarColor } from '../../lib/criteria';
import { FileText, BarChart2, ArrowRight, Building2, AlertTriangle, ShieldAlert } from 'lucide-react';

// Tope de evaluaciones para las que se pide el detalle completo al cargar
// el dashboard (necesario para derivar incidentes).
const MAX_EVALS_CON_DETALLE = 25;

// #HallazgosCliente-1.4 — fecha de referencia del historial: published_at con
// respaldo en la fecha capturada por el levantador. null cuando no hay ninguna
// o no se puede interpretar.
export function evaluationDateMs(evaluation) {
  const raw = evaluation?.published_at || evaluation?.general_fields?.fecha;
  const ms = raw ? Date.parse(raw) : NaN;
  return Number.isFinite(ms) ? ms : null;
}

// Orden cronológico descendente entre proyectos distintos; las evaluaciones
// sin fecha van al final. Devuelve una copia: no muta el state.
export function sortHistorial(evaluations) {
  const byVersion = (a, b) => (b.version ?? 0) - (a.version ?? 0);
  return [...evaluations].sort((a, b) => {
    const da = evaluationDateMs(a);
    const db = evaluationDateMs(b);
    if (da === null && db === null) return byVersion(a, b);
    if (da === null) return 1;
    if (db === null) return -1;
    return db - da || byVersion(a, b);
  });
}

function IncidentRow({ incident }) {
  const severityBg = incident.severity === 'Crítico' ? 'bg-red-100 text-red-800 border-red-200' : 'bg-orange-100 text-orange-800 border-orange-200';
  return (
    <div className="flex items-start gap-3 px-5 py-3 border-b border-slate-50" data-testid={`incident-${incident.id}`}>
      <div className={`flex-shrink-0 text-xs font-bold px-2 py-0.5 rounded-full border ${severityBg}`}>
        {incident.severity}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-soem-navy">{incident.title}</p>
        <p className="text-xs text-slate-500 mt-0.5">{incident.evaluation} · {incident.date}</p>
      </div>
    </div>
  );
}

export default function ClienteDashboard() {
  const { activeUser } = useAuth();
  const navigate = useNavigate();
  const [evaluations, setEvaluations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [activeTab, setActiveTab] = useState('evaluaciones');

  // #HallazgosCliente-1.7 — antes `.catch(() => {})` escondía fallos de
  // /evaluations y /projects (token vencido, 5xx) y el cliente veía "sin
  // proyectos asignados". Mismo patrón que Events.jsx: alerta + Reintentar.
  useEffect(() => {
    let current = true;
    setLoading(true);
    setError('');
    Promise.all([
      evaluationsAPI.list(),
      projectsAPI.list(),
      // #ClienteJerarquia — los eventos ahora son parte de la vista, pero
      // si /events fallara no debe tumbar proyectos ni evaluaciones (el
      // Promise.all rechaza completo): por eso el catch por-llamada.
      // El limit explícito porque eventsAPI.list no aplica LIST_ALL_PARAMS
      // y el backend default a 50.
      eventsAPI.list({ limit: 1000 }).catch(() => ({ data: { items: [] } })),
    ])
      .then(async ([e, p, ev]) => {
        if (!current) return;
        // El backend ya fuerza status=publicada para el rol cliente; el
        // filtro se queda como red de seguridad. NUNCA mostrar borradores:
        // es información interna sin validar.
        const publicadas = (e.data.items || []).filter(x => x.status === 'publicada');
        setProjects(p.data.items || []);
        setEvents(ev.data.items || []);

        // #ClienteIncidentes — GET /api/evaluations NO devuelve `items`
        // (la proyección del listado los excluye), así que los contadores
        // de incidentes y la bitácora leían siempre 0, hubiera o no
        // criterios reprobados. El detalle sí trae las secciones con sus
        // puntajes, y por eso se piden aparte. Se acota a las más
        // recientes para no disparar N peticiones en clientes grandes.
        const conDetalle = await Promise.all(
          publicadas.slice(0, MAX_EVALS_CON_DETALLE).map(x =>
            evaluationsAPI.get(x.id).then(r => r.data).catch(() => x)
          )
        );
        const resto = publicadas.slice(MAX_EVALS_CON_DETALLE);
        if (current) setEvaluations([...conDetalle, ...resto]);
      })
      .catch(err => { if (current) setError(errorMessage(err, 'No se pudo cargar el portal')); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [retry]);

  const latestByProject = {};
  evaluations.forEach(e => {
    if (!latestByProject[e.project_id] || e.version > latestByProject[e.project_id].version)
      latestByProject[e.project_id] = e;
  });
  const projectName = id => projects.find(p => p.id === id)?.name || 'Proyecto';

  // Derive incidents from published evaluations.
  // #TemplateEvalRender — se resuelve con getEvalIncidents porque las
  // evaluaciones de plantilla guardan `question_id` y no `criterio_id`.
  const incidents = evaluations.flatMap(ev => {
    const evName = ev.general_fields?.instalacion || projectName(ev.project_id);
    const date = ev.published_at ? new Date(ev.published_at).toLocaleDateString('es-MX') : '—';
    return getEvalIncidents(ev, { evaluation: `${evName} v${ev.version}`, date });
  });
  const criticalIncidents = incidents.filter(i => i.severity === 'Crítico');
  const majorIncidents = incidents.filter(i => i.severity === 'Mayor');
  // #ClienteJerarquia — con 0 evaluaciones publicadas, los contadores de
  // incidentes en rojo/naranja se leían como "dato malo" cuando en
  // realidad todavía no hay nada que medir. Con esta bandera las tarjetas
  // derivadas de evaluaciones muestran guion + explicación en vez de 0.
  const hasPublished = evaluations.length > 0;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-4 border-soem-primary border-t-transparent" /></div>;
  if (error) return (
    <div role="alert" className="soem-card p-5 flex flex-wrap items-center gap-3" data-testid="cliente-dashboard-error">
      <AlertTriangle size={18} className="text-red-600 flex-shrink-0" />
      <p className="text-sm text-slate-700 flex-1">{error}</p>
      <button type="button" className="soem-btn-secondary px-3 py-1.5 text-sm" onClick={() => setRetry(n => n + 1)}>Reintentar</button>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="cliente-dashboard" id="dashboard-cliente">
      <div>
        <h1 className="text-2xl font-bold text-soem-navy">Portal de Evaluaciones</h1>
        <p className="text-slate-500 text-sm mt-1">Bienvenido, {activeUser?.name}</p>
      </div>

      {/* Stats — las dos primeras salen de proyectos/eventos (siempre con
          datos reales); las tres siguientes dependen de que ya exista al
          menos una evaluación publicada. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
        <div className="soem-card-accent p-5">
          <p className="soem-label mb-1">Mis Proyectos</p>
          <p className="text-3xl font-black text-soem-navy">{projects.length}</p>
          <p className="text-xs text-slate-400 mt-1">Vinculados a tu cuenta</p>
        </div>
        <div className="soem-card-accent p-5">
          <p className="soem-label mb-1">Eventos Registrados</p>
          <p className="text-3xl font-black text-soem-navy">{events.length}</p>
          <p className="text-xs text-slate-400 mt-1">
            {events.length > 0 ? 'En tus proyectos' : 'Se registran al programarse'}
          </p>
        </div>
        <div className="soem-card-accent p-5">
          <p className="soem-label mb-1">Evaluaciones Publicadas</p>
          <p className={`text-3xl font-black ${hasPublished ? 'text-soem-navy' : 'text-slate-300'}`}>
            {evaluations.length}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            {hasPublished
              ? `${Object.keys(latestByProject).length} locación(es) evaluada(s)`
              : 'SOEM está validando las de tus eventos'}
          </p>
        </div>
        <div className={`soem-card p-5 border-t-4 ${hasPublished ? 'border-t-red-600' : 'border-t-slate-200'}`}>
          <p className="soem-label mb-1">Incidentes Críticos</p>
          <p className={`text-3xl font-black ${hasPublished ? 'text-red-600' : 'text-slate-300'}`}>
            {hasPublished ? criticalIncidents.length : '—'}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            {hasPublished ? 'Detectados en tus evaluaciones' : 'Se calculan con la primera publicación'}
          </p>
        </div>
        <div className={`soem-card p-5 border-t-4 ${hasPublished ? 'border-t-orange-500' : 'border-t-slate-200'}`}>
          <p className="soem-label mb-1">Incidentes Mayores</p>
          <p className={`text-3xl font-black ${hasPublished ? 'text-orange-600' : 'text-slate-300'}`}>
            {hasPublished ? majorIncidents.length : '—'}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            {hasPublished ? 'Detectados en tus evaluaciones' : 'Se calculan con la primera publicación'}
          </p>
        </div>
      </div>

      {/* #ClienteJerarquia — la jerarquía va ARRIBA de las pestañas y se
          renderiza siempre: es lo único que el cliente ve cuando todavía
          no tiene evaluaciones publicadas. */}
      <ProjectHierarchy
        projects={projects}
        events={events}
        evaluations={evaluations}
        onOpenEvaluation={id => navigate(`/cliente/evaluaciones/${id}`)}
      />

      {/* Trend chart — con menos de 2 publicadas pintaba un eje vacío; se
          reemplaza por un placeholder que dice qué falta para verla. */}
      {evaluations.length >= 2 ? (
        <div className="soem-card p-5">
          <p className="font-bold text-soem-navy mb-1">Evolución del Puntaje de Seguridad</p>
          <p className="text-slate-400 text-xs mb-3">Histórico de evaluaciones publicadas</p>
          <VersionTrendChart evaluations={evaluations} />
        </div>
      ) : (
        <div className="soem-card p-5" data-testid="trend-placeholder">
          <p className="font-bold text-soem-navy mb-1">Evolución del Puntaje de Seguridad</p>
          <p className="text-slate-400 text-xs mb-3">Histórico de evaluaciones publicadas</p>
          <div className="border border-dashed border-slate-200 rounded-sm px-5 py-8 text-center">
            <BarChart2 size={28} className="text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500 max-w-md mx-auto">
              {hasPublished
                ? 'Con una sola evaluación publicada todavía no hay tendencia que graficar. La gráfica se activa cuando se publique la siguiente.'
                : 'La gráfica se activa cuando SOEM publique tus primeras evaluaciones.'}
            </p>
          </div>
        </div>
      )}

      {/* Tabs — #TourAnchors: id para el tour guiado */}
      <div id="mis-evaluaciones" className="flex gap-1 border-b border-slate-200">
        {[
          { id: 'evaluaciones', label: 'Locaciones Evaluadas', icon: Building2 },
          { id: 'historial', label: 'Historial Completo', icon: FileText },
          { id: 'bitacora', label: `Bitácora de Incidentes (${incidents.length})`, icon: ShieldAlert, anchor: 'tab-bitacora' },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} id={tab.anchor}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${activeTab === tab.id ? 'border-soem-primary text-soem-primary' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid={`tab-${tab.id}`}>
            <tab.icon size={14} /> {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'evaluaciones' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {Object.keys(latestByProject).length === 0 ? (
            <div className="soem-card p-12 text-center col-span-2">
              <Building2 size={40} className="text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 font-medium">Las evaluaciones aparecerán aquí cuando SOEM las publique.</p>
              <p className="text-slate-400 text-sm mt-1">
                Mientras tanto puedes revisar tus proyectos y eventos en la sección Mis Proyectos, aquí arriba.
              </p>
            </div>
          ) : (
            Object.values(latestByProject).map(e => (
              <div key={e.id} className="soem-card hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/cliente/evaluaciones/${e.id}`)} data-testid={`cliente-eval-card-${e.id}`}>
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-soem-navy">{e.general_fields?.instalacion || projectName(e.project_id)}</h3>
                      <p className="text-slate-400 text-xs mt-0.5">{projectName(e.project_id)}</p>
                    </div>
                    <SemaforoBadge semaforo={e.semaforo} percentage={evaluationPercentage(e)} size="md" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ScoreSummary evaluation={e} />
                      <StatusBadge status={displayStatus(e)} />
                      <span className="bg-slate-100 text-slate-500 text-xs px-1.5 py-0.5 rounded font-mono">v{e.version}</span>
                    </div>
                    <span className="text-soem-primary text-xs font-semibold flex items-center gap-1">Ver <ArrowRight size={13} /></span>
                  </div>
                </div>
                <div className="px-5 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-slate-100 rounded-full h-2">
                      {/* #Fase0-B4 — corte único del semáforo (C1); antes ≥81/≥61/≥41 escrito aquí. */}
                      <div className="h-2 rounded-full transition-all"
                        style={{
                          width: `${evaluationPercentage(e) ?? 0}%`,
                          backgroundColor: getSemaforoBarColor(evaluationPercentage(e)),
                        }} />
                    </div>
                    <span className="text-xs font-mono font-bold text-slate-500">
                      {percentageLabel(evaluationPercentage(e), 1)}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'historial' && (
        <div className="soem-card">
          <div className="divide-y divide-slate-50">
            {evaluations.length === 0 ? (
              <p className="text-slate-500 text-sm p-5">
                Las evaluaciones aparecerán aquí cuando SOEM las publique.
              </p>
            ) : (
              // #ClienteJerarquia — `evaluations.sort(...)` ordenaba EN SITIO
              // el arreglo del state durante el render, reordenando también
              // los datos que consumen las tarjetas y la jerarquía. Copia.
              // #HallazgosCliente-1.4 — cronológico, no por versión.
              sortHistorial(evaluations).map(e => (
                <div key={e.id} className="flex items-center justify-between px-5 py-3 hover:bg-blue-50/40 cursor-pointer transition-colors"
                  onClick={() => navigate(`/cliente/evaluaciones/${e.id}`)} data-testid={`eval-history-${e.id}`}>
                  <div>
                    <p className="font-semibold text-soem-navy text-sm">
                      {e.general_fields?.instalacion || projectName(e.project_id)}
                      <span className="ml-2 bg-slate-100 text-slate-500 text-xs px-1.5 py-0.5 rounded font-mono">v{e.version}</span>
                    </p>
                    {/* #M3 — `A || B ? C : D` parsea como `(A || B) ? C : D`:
                        general_fields.fecha nunca se usaba como valor, solo
                        para decidir la condición, y el resultado mostrado
                        siempre era new Date(published_at) — "Invalid Date"
                        cuando solo existía la fecha capturada por el
                        levantador (sin published_at). */}
                    <p className="text-slate-400 text-xs">
                      {(() => {
                        const raw = e.general_fields?.fecha || e.published_at;
                        return raw ? new Date(raw).toLocaleDateString('es-MX') : '—';
                      })()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                      <SemaforoBadge semaforo={e.semaforo} percentage={evaluationPercentage(e)} size="sm" />
                    <ArrowRight size={14} className="text-slate-300" />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'bitacora' && (
        <div className="space-y-4" data-testid="incidents-tab">
          {incidents.length === 0 ? (
            <div className="soem-card p-12 text-center">
              <ShieldAlert size={40} className="text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 font-medium">
                {hasPublished ? 'Sin incidentes registrados.' : 'Todavía no hay incidentes que reportar.'}
              </p>
              <p className="text-slate-400 text-sm mt-1">
                {hasPublished
                  ? 'No se han detectado criterios críticos o mayores en 0-2 puntos.'
                  : 'La bitácora se arma con las evaluaciones publicadas. Aparecerá aquí cuando SOEM publique la primera.'}
              </p>
            </div>
          ) : (
            <>
              {criticalIncidents.length > 0 && (
                <div className="soem-card overflow-hidden">
                  <div className="bg-red-600 px-5 py-3 flex items-center gap-2">
                    <AlertTriangle size={16} className="text-white" />
                    <span className="text-white font-bold text-sm">Incidentes Críticos ({criticalIncidents.length})</span>
                  </div>
                  {criticalIncidents.map(i => <IncidentRow key={i.id} incident={i} />)}
                </div>
              )}
              {majorIncidents.length > 0 && (
                <div className="soem-card overflow-hidden">
                  <div className="bg-orange-500 px-5 py-3 flex items-center gap-2">
                    <AlertTriangle size={16} className="text-white" />
                    <span className="text-white font-bold text-sm">Incidentes Mayores ({majorIncidents.length})</span>
                  </div>
                  {majorIncidents.map(i => <IncidentRow key={i.id} incident={i} />)}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
